import React, { useEffect, useState } from 'react';
import { plainText } from './richText';
import { Link } from 'react-router-dom';

/**
 * Renders a section that shows content from the brand's library.
 *
 * The server sends `libraryEntries` already resolved, so this only has to
 * arrange them. Entries come from eleven different collections with different
 * field names, so rather than a renderer per type, each entry is reduced to
 * the four things every one of them has some version of — a title, some
 * words, a picture, a role or date — and laid out from that. A new content
 * type then works here without any change.
 *
 * Plain elements and inline styles: this runs on the brand's own domain,
 * outside our theme, and must paint the colours the brand chose.
 */

// Field names in rough order of preference, matched against whatever the
// entry actually carries.
const pick = (entry, names) => {
  for (const n of names) {
    const v = entry?.[n];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const normalise = (entry) => ({
  id: entry._id,
  title: pick(entry, ['title', 'name', 'heading']),
  body: pick(entry, ['content', 'description', 'bio', 'text', 'message', 'comment']),
  image: pick(entry, ['image', 'logo', 'photo', 'avatar', 'picture']),
  meta: pick(entry, ['role', 'position', 'author', 'date', 'company']),
});

function EntryCard({ entry, textColor, stacked, linkTo }) {
  const e = normalise(entry);
  const body = (
    <article
      style={{
        display: 'flex',
        flexDirection: stacked ? 'row' : 'column',
        gap: stacked ? 16 : 10,
        alignItems: stacked ? 'flex-start' : 'stretch',
        textAlign: 'left',
      }}
    >
      {e.image && (
        <img
          src={e.image}
          alt={e.title}
          loading='lazy'
          style={{
            width: stacked ? 96 : '100%',
            height: stacked ? 96 : 180,
            objectFit: 'cover',
            borderRadius: 10,
            flexShrink: 0,
            background: 'rgba(127,127,127,0.12)',
          }}
        />
      )}
      <div style={{ minWidth: 0 }}>
        {e.title && (
          <h3 style={{ margin: '0 0 4px', fontSize: 18, lineHeight: 1.3, color: textColor }}>
            {e.title}
          </h3>
        )}
        {e.meta && (
          <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 6, color: textColor }}>
            {e.meta}
          </div>
        )}
        {e.body && (
          // A teaser, so the writing's own headings and bullets are stripped
          // rather than drawn: cards in a row have to stay the same shape as
          // each other, and one entry starting with a heading would make its
          // card twice the height of its neighbours.
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, opacity: 0.85, color: textColor }}>
            {plainText(e.body)}
          </p>
        )}
      </div>
    </article>
  );

  // Only linked when the section names a page to open. A team member or a
  // partner logo has nothing more to show, and making it clickable would
  // promise a page that does not exist.
  if (!linkTo) return body;
  return (
    <Link
      to={linkTo}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      {body}
    </Link>
  );
}

/**
 * The heading a content type introduces itself with, edited on the content
 * type's own screen in the dashboard. Optional in every part — a list with
 * no introduction is a perfectly good list.
 */
function LibraryIntro({ intro, textColor }) {
  if (!intro) return null;
  return (
    <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 32px', color: textColor }}>
      {intro.image && (
        <img
          src={intro.image}
          alt=''
          style={{
            width: '100%', maxWidth: 420, height: 'auto',
            borderRadius: 12, marginBottom: 18, display: 'block',
            marginLeft: 'auto', marginRight: 'auto',
          }}
        />
      )}
      {intro.title && (
        <h2 style={{
          fontSize: 'clamp(24px, 3.2vw, 34px)', lineHeight: 1.2,
          margin: '0 0 10px', fontWeight: 700, textWrap: 'balance',
        }}>
          {intro.title}
        </h2>
      )}
      {intro.description && (
        <p style={{ fontSize: 16, lineHeight: 1.65, margin: 0, opacity: 0.8, whiteSpace: 'pre-wrap' }}>
          {plainText(intro.description)}
        </p>
      )}
    </div>
  );
}

export default function LibrarySection({ section, textColor }) {
  const entries = section.libraryEntries || [];
  const intro = section.libraryIntro || null;
  const display = section.source?.display || 'grid';
  const rotate = Math.max(2, Number(section.source?.rotateSeconds) || 4);
  const perView = Math.min(6, Math.max(1, Number(section.source?.perView) || 1));
  const linkSlug = section.source?.linkPageSlug || '';
  const [index, setIndex] = useState(0);

  const linkFor = (entry) => (linkSlug && entry.slug ? `/${linkSlug}/${entry.slug}` : '');

  // Rotation is skipped when there is nothing to rotate through, and when the
  // visitor has asked for reduced motion — a carousel that moves on its own
  // is exactly what that setting is about.
  const shouldRotate =
    display === 'carousel' &&
    entries.length > perView &&
    !(typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    if (!shouldRotate) return undefined;
    const pages = Math.max(1, Math.ceil(entries.length / perView));
    const id = setInterval(() => setIndex((i) => (i + 1) % pages), rotate * 1000);
    return () => clearInterval(id);
  }, [shouldRotate, entries.length, rotate, perView]);

  // An empty library section renders nothing at all. A visitor should never
  // meet an empty band with a heading over it because the owner has not
  // written their team page yet.
  if (entries.length === 0) return null;

  if (display === 'carousel') {
    // Advances a whole page of items at a time, so "3 at a time, changing
    // every 3 seconds" shows 1-2-3 then 4-5-6 rather than sliding by one and
    // repeating most of what was just read.
    const pages = Math.max(1, Math.ceil(entries.length / perView));
    const start = (index % pages) * perView;
    const shown = entries.slice(start, start + perView);

    return (
      <div>
        <LibraryIntro intro={intro} textColor={textColor} />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${perView > 1 ? 230 : 280}px, 1fr))`,
            gap: 24,
          }}
        >
          {shown.map((e) => (
            <EntryCard
              key={e._id}
              entry={e}
              textColor={textColor}
              stacked={perView === 1}
              linkTo={linkFor(e)}
            />
          ))}
        </div>
        {pages > 1 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                type='button'
                aria-label={`Show group ${i + 1} of ${pages}`}
                onClick={() => setIndex(i)}
                style={{
                  width: 7, height: 7, padding: 0, borderRadius: '50%', border: 'none',
                  cursor: 'pointer',
                  background: textColor,
                  opacity: i === index % pages ? 0.9 : 0.3,
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (display === 'list') {
    return (
      <div>
        <LibraryIntro intro={intro} textColor={textColor} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {entries.map((e) => (
            <EntryCard key={e._id} entry={e} textColor={textColor} stacked linkTo={linkFor(e)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <LibraryIntro intro={intro} textColor={textColor} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
          gap: 24,
        }}
      >
        {entries.map((e) => (
          <EntryCard key={e._id} entry={e} textColor={textColor} linkTo={linkFor(e)} />
        ))}
      </div>
    </div>
  );
}
